import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TextVariant, TEXT_STYLES } from '@components/shared/design/Typography';
import LazyLoadImage from '@components/shared/LazyLoadImage';
import LinkButton from '@components/shared/LinkButton';
import LinkTranslated from '@components/shared/LinkTranslated';
import { Gradient2Style } from '@components/shared/SharedStyles';
import { MEDIA } from '@constants';
import { PageSlice, SliceThreeStatistics, SliceThreeStatisticsItem } from '@contracts';
import IconExternalLink from '@icons/external_link.svg';

interface Props {
  slice: PageSlice;
}

const ThreeStatistics: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceThreeStatistics;
  const sliceItems = slice.items as SliceThreeStatisticsItem[];
  const bigNumberColor = sliceDetails.big_number_color;
  const backgroundColor = sliceDetails.background_color;

  return (
    <>
      <ThreeStatisticsContainer $backgroundColor={backgroundColor}>
        <Wrapper>
          <Heading>
            <PrismicRichText field={sliceDetails.heading.richText} />
          </Heading>
          <ItemsWrapper>
            {sliceItems.map((item: any, index: number) => (
              <ThreeStatisticsItem key={index}>
                {item.image?.url && <LazyLoadImage src={item.image.url} alt={item.image.alt} />}
                <BigNumber $color={bigNumberColor}>{item.big_number}</BigNumber>
                <Description>{item.stat_description.text}</Description>
                {item.source_url && (
                  <StyledLinkTranslated to={item.source_url} target="_blank" rel="noopener noreferrer">
                    {t('components.source')}
                    <IconExternalLink />
                  </StyledLinkTranslated>
                )}
              </ThreeStatisticsItem>
            ))}
          </ItemsWrapper>
          {sliceDetails.button_url && (
            <LinkButton to={sliceDetails.button_url} isInline>
              {sliceDetails.button_text_key}
            </LinkButton>
          )}
        </Wrapper>
      </ThreeStatisticsContainer>
    </>
  );
};

export default ThreeStatistics;

const ThreeStatisticsContainer = styled.section<{ $backgroundColor: string }>`
  color: ${Colors.navyBlue};
  padding: 120px 0;
  background-color: ${(props) => props.$backgroundColor || Colors.cream};

  > * {
    max-width: 1400px;
  }

  ${MEDIA.tablet} {
    padding: 80px 0;
  }
`;

const BigNumber = styled.h3<{ $color: string }>`
  ${TEXT_STYLES[TextVariant.Heading1]};
  color: ${(props) => props.$color || Colors.gold};

  ${MEDIA.mobileM} {
    ${TEXT_STYLES[TextVariant.Heading3]}
  }
`;

const Heading = styled.h2`
  > * {
    ${TEXT_STYLES[TextVariant.Heading4]};
    color: inherit;
    text-align: center;
    margin-bottom: 4rem;
  }
  & span.gradient2 {
    ${Gradient2Style}
  }

  ${MEDIA.laptopL} {
    width: 100%;
  }

  ${MEDIA.tablet} {
    > * {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ItemsWrapper = styled.div`
  display: flex;
  column-gap: 4rem;
  justify-content: center;
  margin-bottom: 32px;

  @media (max-width: 1000px) {
    flex-direction: column;
    row-gap: 4rem;
    max-width: 430px;
  }

  ${MEDIA.mobileM} {
    display: flex;
    row-gap: 32px;
    justify-content: center;
  }
`;

const ThreeStatisticsItem = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  align-items: center;
  text-align: center;
  width: 100%;
  color: inherit;

  & > img {
    margin-top: 40px;
    height: 5rem;
  }
`;

const Description = styled.p`
  ${TEXT_STYLES[TextVariant.BodyRegularXL]}
  color: inherit;
`;

const StyledLinkTranslated = styled(LinkTranslated)`
  display: flex;
  align-items: center;
  gap: 4px;
  ${TEXT_STYLES[TextVariant.BodyBoldMD]}
  color: ${Colors.pink};

  > svg {
    height: 16px;
    width: 16px;
    margin-top: -2px;
  }

  &:hover {
    text-decoration: none;
  }
`;
